import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { forwardRef, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { outlineTemplate, spacingsTemplate, typographyMixin, useToken } from '@equinor/eds-utils';
import { inputToken } from './Input.tokens.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useEds } from '../EdsProvider/eds.context.js';

var _excluded = ["variant", "disabled", "type", "leftAdornments", "rightAdornments", "readOnly", "className", "style", "leftAdornmentsProps", "rightAdornmentsProps", "leftAdornmentsWidth", "rightAdornmentsWidth"];
var Container = styled.div.withConfig({
  displayName: "Input__Container",
  componentId: "sc-1ykv024-0"
})(function (_ref) {
  var _entities$adornment, _entities$adornment$s;
  var $token = _ref.$token,
    disabled = _ref.disabled,
    readOnly = _ref.readOnly;
  var states = $token.states,
    entities = $token.entities;
  return css(["--eds-input-adornment-color:", ";--eds-input-color:", ";position:relative;height:", ";width:", ";display:flex;flex-direction:row;border:none;box-sizing:border-box;box-shadow:", ";background:var(--eds-input-background,", ");", " &:focus-within{--eds-input-adornment-color:", ";box-shadow:none;", "}", " ", " & > input{overflow:hidden;white-space:nowrap;text-overflow:ellipsis;}"], entities.adornment.typography.color, $token.typography.color, $token.height, $token.width, $token.boxShadow, $token.background, outlineTemplate($token.outline), (_entities$adornment = entities.adornment) === null || _entities$adornment === void 0 ? void 0 : (_entities$adornment$s = _entities$adornment.states.focus) === null || _entities$adornment$s === void 0 ? void 0 : _entities$adornment$s.outline.color, outlineTemplate(states.focus.outline), disabled && css(["--eds-input-adornment-color:", ";--eds-input-color:", ";cursor:not-allowed;box-shadow:none;"], states.disabled.typography.color, states.disabled.typography.color), readOnly && css({
    background: states.readOnly.background,
    boxShadow: states.readOnly.boxShadow
  }));
});
var StyledInput = styled.input.withConfig({
  displayName: "Input__StyledInput",
  componentId: "sc-1ykv024-1"
})(function (_ref2) {
  var $token = _ref2.$token,
    $paddingLeft = _ref2.$paddingLeft,
    $paddingRight = _ref2.$paddingRight;
  return css(["width:100%;border:none;background:transparent;", " ", " outline:none;padding-left:", ";padding-right:", ";&::placeholder{color:", ";}&:disabled{color:var(--eds-input-color);cursor:not-allowed;}"], spacingsTemplate($token.spacings), typographyMixin($token.typography), $paddingLeft, $paddingRight, $token.entities.placeholder.typography.color);
});
var Adornments = styled.div.withConfig({
  displayName: "Input__Adornments",
  componentId: "sc-1ykv024-2"
})(function (_ref3) {
  var $token = _ref3.$token;
  return css(["position:absolute;top:", ";bottom:", ";display:flex;align-items:center;", " color:var(--eds-input-adornment-color);"], $token.spacings.top, $token.spacings.bottom, typographyMixin($token.entities.adornment.typography));
});
var LeftAdornments = styled(Adornments).withConfig({
  displayName: "Input__LeftAdornments",
  componentId: "sc-1ykv024-3"
})(function (_ref4) {
  var $token = _ref4.$token;
  return css(["left:0;padding-left:", ";"], $token.entities.adornment.spacings.left);
});
var RightAdornments = styled(Adornments).withConfig({
  displayName: "Input__RightAdornments",
  componentId: "sc-1ykv024-4"
})(function (_ref5) {
  var $token = _ref5.$token;
  return css(["right:0;padding-right:", ";"], $token.entities.adornment.spacings.right);
});
var Input = /*#__PURE__*/forwardRef(function Input(_ref6, ref) {
  var variant = _ref6.variant,
    _ref6$disabled = _ref6.disabled,
    disabled = _ref6$disabled === void 0 ? false : _ref6$disabled,
    _ref6$type = _ref6.type,
    type = _ref6$type === void 0 ? 'text' : _ref6$type,
    leftAdornments = _ref6.leftAdornments,
    rightAdornments = _ref6.rightAdornments,
    readOnly = _ref6.readOnly,
    className = _ref6.className,
    style = _ref6.style,
    leftAdornmentsProps = _ref6.leftAdornmentsProps,
    rightAdornmentsProps = _ref6.rightAdornmentsProps,
    leftAdornmentsWidth = _ref6.leftAdornmentsWidth,
    rightAdornmentsWidth = _ref6.rightAdornmentsWidth,
    other = _objectWithoutProperties(_ref6, _excluded);
  var inputVariant = inputToken[variant] ? inputToken[variant] : inputToken.input;
  var _useEds = useEds(),
    density = _useEds.density;
  var _token = useToken({
    density: density
  }, inputVariant)();
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    rightAdornmentsRef = _useState2[0],
    setRightAdornmentsRef = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    leftAdornmentsRef = _useState4[0],
    setLeftAdornmentsRef = _useState4[1];
  var token = useCallback(function () {
    var _leftAdornmentsWidth = leftAdornmentsWidth || (leftAdornmentsRef ? leftAdornmentsRef.clientWidth : 0);
    var _rightAdornmentsWidth = rightAdornmentsWidth || (rightAdornmentsRef ? rightAdornmentsRef.clientWidth : 0);
    return _objectSpread(_objectSpread({}, _token), {}, {
      spacings: _objectSpread(_objectSpread({}, _token.spacings), {}, {
        left: "".concat(_leftAdornmentsWidth + parseInt(_token.spacings.left), "px"),
        right: "".concat(_rightAdornmentsWidth + parseInt(_token.spacings.right), "px")
      })
    });
  }, [leftAdornmentsWidth, leftAdornmentsRef, rightAdornmentsWidth, rightAdornmentsRef, _token])();
  var inputProps = _objectSpread({
    ref: ref,
    type: type,
    disabled: disabled,
    readOnly: readOnly,
    $token: token,
    style: {
      resize: 'none'
    }
  }, other);
  var containerProps = {
    disabled: disabled,
    readOnly: readOnly,
    className: className,
    style: style,
    $token: token
  };
  var _leftAdornmentProps = _objectSpread(_objectSpread({}, leftAdornmentsProps), {}, {
    ref: setLeftAdornmentsRef,
    $token: token
  });
  var _rightAdornmentProps = _objectSpread(_objectSpread({}, rightAdornmentsProps), {}, {
    ref: setRightAdornmentsRef,
    $token: token
  });
  return (
    /*#__PURE__*/
    // Not using <ThemeProvider> because of cascading styling messing with adornments
    jsxs(Container, _objectSpread(_objectSpread({}, containerProps), {}, {
      children: [leftAdornments ? /*#__PURE__*/jsx(LeftAdornments, _objectSpread(_objectSpread({}, _leftAdornmentProps), {}, {
        children: leftAdornments
      })) : null, /*#__PURE__*/jsx(StyledInput, _objectSpread({
        $paddingLeft: token.spacings.left,
        $paddingRight: token.spacings.right
      }, inputProps)), rightAdornments ? /*#__PURE__*/jsx(RightAdornments, _objectSpread(_objectSpread({}, _rightAdornmentProps), {}, {
        children: rightAdornments
      })) : null]
    }))
  );
});

export { Input };
